import React from "react";
import {
  Code,
  FileText,
  GalleryVerticalEnd,
  PackageCheck,
  ShieldCheck,
  SquarePen,
  TypeOutline,
  UserRound,
} from "lucide-react";
import { Layers, Cable } from "lucide-react";
import { GeneralInfoSkeleton } from "../plugin/GeneralInfoSkeleton";

type ViewProps = {
  pluginName: string;
  version: string;
  tags: string[];
  description1: string;
  description2: string;
  ls_version: { minimum: string };
  type: string;
  author: string;
  productType: string;
  ls_product_type: string[];
  pluginDependency: { name: string; id: string }[];
  pluginLogo?: string;
  onEdit: () => void;
  isNewPlugin: boolean;
};

export const GeneralInfoView: React.FC<ViewProps> = ({
  pluginName,
  version,
  tags,
  description1,
  description2,
  ls_version,
  type,
  author,
  productType,
  ls_product_type,
  pluginDependency,
  pluginLogo,
  onEdit,
  isNewPlugin,
}) => {
  const isDependency = pluginDependency?.some((item) => item.name);
  if (!isNewPlugin && !pluginName)
    return <GeneralInfoSkeleton className="min-h-[70vh]" v={"2.0"} />;

  return (
    <div className="p-4 space-y-5 cursor-default h-[70vh] overflow-y-auto bg-background">
      {/* Hero */}
      <div className="flex items-start gap-5 border-t shadow rounded-2xl p-3 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-right scale-100 blur-[10px] opacity-10 overflow-hidden"
          style={{
            backgroundImage: `url(${pluginLogo || ``})`,
          }}
        />
        <img
          src={
            pluginLogo || `${process.env.STATIC_PATH}/images/plugin-default.jpg`
          }
          alt={`${pluginName} logo`}
          className="w-[160px] h-[160px] rounded-2xl object-cover flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 w-full mb-1">
            <p className="text-[22px] font-extrabold  text-foreground truncate [text-shadow:0_2px_4px_rgba(0,0,0,0.1)]">
              {pluginName}
            </p>
            <button
              onClick={onEdit}
              className="flex-shrink-0 w-8 h-8 z-[9999] rounded-lg border flex items-center justify-center text-muted-foreground bg-muted hover:text-foreground transition-colors"
            >
              <SquarePen size={14} />
            </button>
          </div>

          {/* primary details */}
          <div className="flex flex-wrap gap-4 mt-2">
            {[
              {
                icon: (
                  <UserRound
                    size={16}
                    className="text-primary"
                    strokeWidth={2.5}
                  />
                ),
                label: author,
              },
              {
                icon: (
                  <GalleryVerticalEnd size={16} className="text-primaryGreen" />
                ),
                label: version,
              },
              {
                icon: <TypeOutline size={16} className="text-primaryBlue" />,
                label: type,
              },
            ].map(({ icon, label }) => (
              <span
                key={label}
                className="!bg-background flex text-sm items-center gap-2 py-2.5 px-6 shadow rounded-md "
              >
                {icon} {label}
              </span>
            ))}
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div className=" mt-1 h-24 pb-2 py-2 flex flex-col justify-center">
              <span className="text-[11px] text-muted-foreground mr-1 font-mono uppercase">
                Tag{tags.length > 1 ? "s" : ""}
              </span>
              <div className="flex items-start gap-1.5 mt-1">
                {tags.map((tag, i) => (
                  <span
                    key={i}
                    className="flex text-[11px] px-2.5 py-0.5 rounded-2xl bg-border text-muted-foreground border border-primaryBlue/20"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* <div className="border-t shadow" /> */}

      {/* Meta cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        {[
          {
            icon: <ShieldCheck size={18} className="text-primary" />,
            label: "LS version",
            value: `${ls_version.minimum}`,
            text: "primary",
          },
          {
            icon: <Layers size={18} className="text-primaryBlue" />,
            label: "Product type",
            value: productType,
            text: "primaryBlue",
          },
          {
            icon: <PackageCheck size={18} className="text-primaryGreen" />,
            label: "LS product type",
            value: (
              <div className="flex flex-wrap gap-2 mt-0.5 p-1">
                {ls_product_type.map((item) => (
                  <span
                    key={item}
                    className="text-[11px] px-2 py-0.5 bg-lightGreen rounded text-muted-foreground rounded-md font-mono shadow uppercase"
                  >
                    {item}
                  </span>
                ))}
              </div>
            ),
            text: "primaryGreen",
          },
          ...(isDependency
            ? [
                {
                  icon: <Cable size={18} className="text-amber" />,
                  label: "Dependency",
                  value: (
                    <div className="flex flex-wrap gap-1 mt-0.5 p-1">
                      {pluginDependency
                        .filter((d) => d.name)
                        .map((dep) => (
                          <span
                            key={dep.id}
                            className="text-[11px] px-2 py-0.5 bg-lightGray rounded text-muted-foreground rounded-md font-mono"
                          >
                            {dep.name}
                          </span>
                        ))}
                    </div>
                  ),
                  text: "amber",
                },
              ]
            : []),
        ].map(({ icon, label, value, text }) => (
          <div
            key={label}
            className="shadow rounded-xl p-2.5 border border-muted"
          >
            <p
              className={`text-[11px] flex items-center gap-1 mb-1 text-${text} text-sm`}
            >
              {icon} {label}
            </p>
            {typeof value === "string" ? (
              <p className="text-sm text-muted-foreground text-sm p-1 font-mono ">
                {value}
              </p>
            ) : (
              value
            )}
          </div>
        ))}
      </div>

      {/* Description cards */}
      <div className="grid md:grid-cols-2 gap-3">
        {[
          {
            icon: <FileText size={16} />,
            label: "Plugin description",
            text: description1,
          },
          {
            icon: <Code size={18} />,
            label: "Developer description",
            text: description2,
          },
        ].map(({ icon, label, text }) => (
          <div key={label} className="bg-muted rounded-xl p-4">
            <p className="text-[14px] font-semibold text-muted-foreground flex items-center gap-1 mb-2 border-b pb-2">
              {icon} {label}
            </p>
            <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap break-words">
              {text}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
